/* Cart drawer "Edit" pop-up: change the variant of a cart line without leaving the page.
   Click on [data-tmg-cart-edit] -> fetch /products/<handle>?view=tmg-edit -> show it in a modal ->
   Confirm adds the new variant (same quantity, properties and selling plan) and removes the old line. */
(() => {
  if (window.tmgCartEdit) return;

  class TmgCartEdit {
    constructor() {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-tmg-cart-edit]');
        if (!trigger || event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey) return; // keep "open in new tab" working
        event.preventDefault();
        this.open(trigger);
      });
    }

    ensureModal() {
      if (this.modal) return;
      this.modal = document.createElement('div');
      this.modal.className = 'tmg-edit-modal';
      this.modal.hidden = true;
      this.modal.innerHTML = `
        <div class="tmg-edit-modal__overlay" data-edit-close></div>
        <div class="tmg-edit-modal__dialog" role="dialog" aria-modal="true" tabindex="-1">
          <button type="button" class="tmg-edit-modal__close" data-edit-close>
            <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false"><path d="M3.5 3.5l9 9m0-9l-9 9" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/></svg>
          </button>
          <div class="tmg-edit-modal__body"></div>
        </div>`;
      document.body.appendChild(this.modal);
      this.dialog = this.modal.querySelector('.tmg-edit-modal__dialog');
      this.body = this.modal.querySelector('.tmg-edit-modal__body');
      this.closeButton = this.modal.querySelector('.tmg-edit-modal__close');
      this.modal.addEventListener('click', (event) => {
        if (event.target.closest('[data-edit-close]')) this.close();
      });
      // Close on keyup (not keydown): focus returns to the drawer, which itself closes on an Escape keyup.
      this.modal.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.close();
        }
      });
    }

    async open(trigger) {
      const data = trigger.dataset;
      this.trigger = trigger;
      this.line = {
        key: data.lineKey,
        quantity: Number(data.quantity) || 1,
        variantId: Number(data.variantId),
        properties: this.parseProperties(data.properties),
        sellingPlan: data.sellingPlan ? Number(data.sellingPlan) : null,
      };
      this.errorText = data.error || '';
      this.ensureModal();
      this.modal.hidden = false;
      this.modal.classList.add('is-loading');
      this.body.innerHTML = `<p class="tmg-edit-modal__loading" role="status">${data.loading || ''}</p>`;
      document.body.classList.add('overflow-hidden');
      if (typeof trapFocus === 'function') trapFocus(this.dialog, this.closeButton);
      else this.closeButton.focus();

      const token = (this.requestToken = (this.requestToken || 0) + 1);
      try {
        const url = `${data.productUrl}?view=tmg-edit&variant=${encodeURIComponent(data.variantId)}`;
        const response = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        if (token !== this.requestToken || this.modal.hidden) return;
        this.body.innerHTML = html;
        this.modal.classList.remove('is-loading');
        this.root = this.body.querySelector('.tmg-edit');
        if (!this.root) throw new Error('missing pop-up markup');
        this.init();
      } catch (error) {
        console.error('Cart edit pop-up failed', error);
        if (token === this.requestToken) {
          this.modal.classList.remove('is-loading');
          this.body.innerHTML = `<p class="tmg-edit-modal__loading" role="alert">${this.errorText}</p>`;
        }
      }
    }

    parseProperties(raw) {
      try {
        const value = JSON.parse(raw || '{}');
        return value && !Array.isArray(value) && typeof value === 'object' ? value : {};
      } catch (e) {
        return {};
      }
    }

    close() {
      if (!this.modal || this.modal.hidden) return;
      this.requestToken += 1;
      this.modal.hidden = true;
      this.body.innerHTML = '';
      document.body.classList.remove('overflow-hidden');
      // Hand focus back to the cart drawer, which is still open underneath.
      const drawer = document.getElementById('CartDrawer');
      const drawerOpen = document.querySelector('cart-drawer.active');
      if (drawerOpen) {
        // The drawer body is re-rendered after an update, so the old trigger may be gone.
        const target = this.trigger && document.body.contains(this.trigger) ? this.trigger : drawer.querySelector('.drawer__close') || drawer;
        if (typeof trapFocus === 'function') trapFocus(drawer, target);
        else target.focus();
      } else {
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.trigger);
        document.body.classList.remove('overflow-hidden');
      }
      if (drawerOpen) document.body.classList.add('overflow-hidden');
    }

    /* ---- pop-up behaviour ---------------------------------------------------- */
    init() {
      const root = this.root;
      this.variants = JSON.parse(root.querySelector('[data-tmg-edit-variants]').textContent || '[]');
      this.fieldsets = [...root.querySelectorAll('.tmg-edit__option')];
      this.confirmButton = root.querySelector('[data-confirm]');
      this.confirmText = root.querySelector('[data-confirm-text]');
      this.confirmPrice = root.querySelector('[data-confirm-price]');
      this.confirmDefaultText = this.confirmText.textContent;
      this.errorBox = root.querySelector('.tmg-edit__error');
      this.soldOutText = root.dataset.soldOut;
      this.unavailableText = root.dataset.unavailable;
      this.errorText = root.dataset.error || this.errorText;

      root.addEventListener('change', (event) => {
        if (event.target.matches('.tmg-edit__option input')) this.onOptionChange(true);
      });
      this.confirmButton.addEventListener('click', () => this.confirm());
      this.initSlider();
      this.onOptionChange(false);
      this.closeButton.focus();
    }

    selectedOptions() {
      return this.fieldsets.map((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    currentVariant() {
      // A product with a single (default) variant has no option pickers.
      if (!this.fieldsets.length) return this.variants[0] || null;
      const selected = this.selectedOptions();
      return this.variants.find((variant) => variant.options.every((value, index) => value === selected[index])) || null;
    }

    onOptionChange(scrollToMedia) {
      this.fieldsets.forEach((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        const label = fieldset.querySelector('[data-selected-label]');
        if (checked && label) label.textContent = checked.value;
      });
      const variant = this.currentVariant();
      this.hideError();
      if (!variant) {
        this.setConfirm(false, this.unavailableText, '');
        return;
      }
      if (!variant.available) this.setConfirm(false, this.soldOutText, variant.price);
      else this.setConfirm(true, this.confirmDefaultText, variant.price);
      if (scrollToMedia && variant.mediaId) this.goToMedia(variant.mediaId);
    }

    setConfirm(enabled, text, price) {
      this.confirmButton.disabled = !enabled;
      this.confirmText.textContent = text;
      this.confirmPrice.textContent = price;
    }

    showError(message) {
      this.errorBox.textContent = message || this.errorText;
      this.errorBox.hidden = false;
    }

    hideError() {
      this.errorBox.hidden = true;
      this.errorBox.textContent = '';
    }

    /* ---- image slider ------------------------------------------------------------ */
    initSlider() {
      const root = this.root;
      this.slider = root.querySelector('.tmg-edit__slider');
      this.slides = [...root.querySelectorAll('.tmg-edit__slide')];
      this.dots = [...root.querySelectorAll('.tmg-edit__dot')];
      const prev = root.querySelector('.tmg-edit__arrow--prev');
      const next = root.querySelector('.tmg-edit__arrow--next');
      const step = (direction) => this.slider.scrollBy({ left: direction * this.slider.clientWidth, behavior: 'smooth' });
      if (prev) prev.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));
      this.slider.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); step(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); step(1); }
      });
      this.slider.addEventListener('scroll', () => this.updateDots(), { passive: true });
      const variant = this.currentVariant();
      if (variant && variant.mediaId) this.goToMedia(variant.mediaId, true);
      this.updateDots();
    }

    goToMedia(mediaId, instant = false) {
      const index = this.slides.findIndex((slide) => Number(slide.dataset.mediaId) === Number(mediaId));
      if (index < 0) return;
      this.slider.scrollTo({ left: index * this.slider.clientWidth, behavior: instant ? 'auto' : 'smooth' });
    }

    updateDots() {
      if (!this.dots.length) return;
      const index = Math.round(this.slider.scrollLeft / Math.max(this.slider.clientWidth, 1));
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }

    /* ---- update the cart ------------------------------------------------------------ */
    async confirm() {
      const variant = this.currentVariant();
      if (!variant || !variant.available) return;
      if (variant.id === this.line.variantId) {
        this.close();
        return;
      }
      this.confirmButton.disabled = true;
      this.confirmButton.classList.add('is-busy');
      this.hideError();
      try {
        // The shopper may have changed the quantity since the drawer was drawn: read it fresh.
        const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        const current = cart.items.find((item) => item.key === this.line.key);
        if (!current) {
          await this.refreshDrawer();
          this.close();
          return;
        }

        const item = { id: variant.id, quantity: current.quantity };
        if (Object.keys(this.line.properties).length) item.properties = this.line.properties;
        if (this.line.sellingPlan) item.selling_plan = this.line.sellingPlan;
        const added = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });
        if (!added.ok) {
          const detail = await added.json().catch(() => ({}));
          throw new Error(detail.description || detail.message || this.errorText);
        }

        // Only after the new variant is in the cart is the old line removed.
        const changed = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: this.line.key,
            quantity: 0,
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname,
          }),
        });
        if (!changed.ok) throw new Error(this.errorText);
        const state = await changed.json();
        this.renderDrawer(state);
        this.close();
      } catch (error) {
        console.error('Cart edit failed', error);
        this.showError(error && error.message ? error.message : this.errorText);
        this.confirmButton.disabled = false;
      } finally {
        if (this.confirmButton) this.confirmButton.classList.remove('is-busy');
      }
    }

    renderDrawer(state) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function' && state.sections) drawer.renderContents(state);
    }

    async refreshDrawer() {
      const response = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
      const sections = await response.json();
      const state = await (await fetch('/cart.js')).json();
      state.sections = sections;
      this.renderDrawer(state);
    }
  }

  window.tmgCartEdit = new TmgCartEdit();
})();
